import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { CalendarDots, Clock, User, Users, Stack, Stethoscope, XCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import type { DayOverrideMarker } from "@/lib/override-periods";
import type { CalendarSession } from "./types";
import { STATUS_STYLES, STATUS_KEYS, effectiveStatus, parseTime } from "./types";

interface DaySidebarProps {
  selectedDate: Date | null;
  sessions: CalendarSession[];
  dayNames: string[];
  monthNames: string[];
  onReschedule?: (session: CalendarSession) => void;
  onCancel?: (session: CalendarSession) => void;
  /** Usluga sa više članova: terapeut/admin mijenja emailove ostalih članova. */
  onEditMembers?: (session: CalendarSession) => void;
  onWithdrawCancelRequest?: (session: CalendarSession) => void;
  onReleaseHold?: (id: string) => void;
  onExtendHold?: (id: string, holdHours: number) => void;
  showTherapist?: boolean;
  /** Admin može otkazati hitnu sesiju; premještanje hitne sesije ostaje skriveno. */
  allowEmergencyCancel?: boolean;
  /** Izuzeci terapeuta za izabrani dan (admin kalendar sa izabranim terapeutom). */
  markers?: DayOverrideMarker[];
}

const MARKER_NOTE_STYLES: Record<DayOverrideMarker["kind"], string> = {
  off: "border-rose-200 bg-rose-50 text-rose-700",
  block: "border-amber-200 bg-amber-50 text-amber-700",
  add: "border-emerald-200 bg-emerald-50 text-emerald-700",
};

export function DaySidebar({
  selectedDate,
  sessions,
  dayNames,
  monthNames,
  onReschedule,
  onCancel,
  onEditMembers,
  onWithdrawCancelRequest,
  onReleaseHold,
  onExtendHold,
  showTherapist = false,
  allowEmergencyCancel = false,
  markers = [],
}: DaySidebarProps) {
  const { t } = useTranslation();
  const sortedSessions = [...sessions].sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt));
  // Workload excludes cancelled / no-show sessions — they aren't time the
  // therapist actually spends, so counting them inflates the day's minutes.
  const isCountable = (s: CalendarSession) => s.status !== "cancelled" && s.status !== "no_show";
  const totalMinutes = sessions.reduce((sum, s) => (isCountable(s) ? sum + s.durationMinutes : sum), 0);
  const nowMs = Date.now();

  return (
    <aside className="w-full xl:w-96">
      <div className="sticky top-6 overflow-hidden rounded-xl border border-border/60 bg-card shadow-sm">
        <div className="border-b border-primary/20 bg-primary px-4 py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-primary-foreground/70">
                {selectedDate ? dayNames[(selectedDate.getDay() + 6) % 7] : "—"}
              </p>
              <p
                className="mt-0.5 truncate text-xl font-bold text-primary-foreground"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                {selectedDate
                  ? `${selectedDate.getDate()}. ${monthNames[selectedDate.getMonth()]}`
                  : t("calendar.selectDay")}
              </p>
            </div>
            <div className="shrink-0 rounded-lg bg-primary-foreground/12 px-2.5 py-1 text-right">
              <p className="text-lg font-bold leading-none tabular-nums text-primary-foreground">{sessions.length}</p>
              <p className="mt-0.5 text-[10px] font-medium uppercase tracking-wide text-primary-foreground/65">
                {t("calendar.totalSessions")}
              </p>
            </div>
          </div>
          {selectedDate && sessions.length > 0 && (
            <div className="mt-3 flex items-center gap-2 text-xs text-primary-foreground/75">
              <Clock className="size-3.5" />
              <span>{t("calendar.periodMinutes", { count: totalMinutes })}</span>
            </div>
          )}
        </div>

        <div className="max-h-[68vh] overflow-y-auto bg-muted/15 px-3 py-3">
          {selectedDate && markers.length > 0 && (
            <div className="mb-2.5 space-y-1.5">
              {markers.map((m, i) => (
                <p
                  key={i}
                  className={cn("rounded-lg border px-3 py-2 text-xs font-medium tabular-nums", MARKER_NOTE_STYLES[m.kind])}
                >
                  {m.kind === "off" ? "Terapeut ovaj dan ne radi (odmor ili slobodan dan)." : m.label}
                </p>
              ))}
            </div>
          )}
          {!selectedDate ? (
            <div className="rounded-xl border border-dashed border-border/70 bg-card px-4 py-8 text-center text-sm text-muted-foreground">
              <CalendarDots className="mx-auto mb-2 size-8 text-muted-foreground/30" />
              <p>{t("calendar.clickDay")}</p>
            </div>
          ) : sessions.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border/70 bg-card px-4 py-8 text-center">
              <CalendarDots className="mx-auto size-8 text-muted-foreground/30" />
              <p className="mt-2 text-sm text-muted-foreground">
                {t("calendar.noSessions")}
              </p>
            </div>
          ) : (
            <div className="space-y-2.5">
              {sortedSessions.map((s) => {
                  const displayStatus = effectiveStatus(s, nowMs);
                  const style = STATUS_STYLES[displayStatus] || STATUS_STYLES.scheduled;
                  const time = parseTime(s.scheduledAt);
                  const endMinutes = time.hour * 60 + time.minute + s.durationMinutes;
                  const endH = Math.floor(endMinutes / 60) % 24;
                  const endM = endMinutes % 60;
                  // Group sessions and emergency sessions aren't managed through
                  // the individual reschedule/cancel actions here.
                  const isRegular = s.kind === undefined || s.kind === "regular";
                  const isScheduled = displayStatus === "scheduled";
                  const canReschedule = isScheduled && isRegular && !!onReschedule;
                  const canCancel =
                    isScheduled && !!onCancel && (isRegular || (allowEmergencyCancel && s.kind === "emergency"));
                  // Hold također: članovi dodani prije uplate dobiju potvrdu kad klijent plati.
                  const canEditMembers =
                    (isScheduled || displayStatus === "hold") &&
                    isRegular &&
                    !!onEditMembers &&
                    (!!s.maxMembers || (s.memberCount ?? 0) > 0);

                  return (
                    <div
                      key={s.id}
                      className={cn(
                        "rounded-xl border bg-card p-3 shadow-sm transition-colors hover:bg-card/95",
                        style.border,
                        s.status === "cancelled" && "opacity-50"
                      )}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <div className={cn("mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg", style.bg)}>
                            <Clock className={cn("size-4", style.text)} />
                          </div>
                          <div className="min-w-0">
                            <p className="text-base font-semibold leading-tight tabular-nums text-foreground">
                              {String(time.hour).padStart(2, "0")}:{String(time.minute).padStart(2, "0")}
                              <span className="mx-1 text-muted-foreground/50">—</span>
                              {String(endH).padStart(2, "0")}:{String(endM).padStart(2, "0")}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {s.durationMinutes} min
                            </p>
                          </div>
                        </div>
                        <div className="flex shrink-0 flex-col items-end gap-1">
                          {s.kind === "emergency" && (
                            <span className="inline-flex items-center rounded-md bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700">
                              Hitna
                            </span>
                          )}
                          {s.kind === "group" && (
                            <span className="inline-flex items-center rounded-md bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold text-violet-700">
                              Grupna
                            </span>
                          )}
                          <span className={cn("inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold", style.bg, style.text)}>
                            <span className={cn("size-1.5 rounded-full", style.dot)} />
                            {t(STATUS_KEYS[displayStatus] || STATUS_KEYS.scheduled)}
                          </span>
                        </div>
                      </div>

                      <div className="mt-3 space-y-1.5 pl-11">
                        {showTherapist && s.therapistName && (
                          <div className="flex items-center gap-1.5 text-xs">
                            <Stethoscope className="size-3 text-muted-foreground" />
                            <span className="font-medium text-foreground">{s.therapistName}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1.5 text-xs">
                          <User className="size-3 text-muted-foreground" />
                          <span className={cn(showTherapist ? "text-muted-foreground" : "font-medium text-foreground")}>
                            {s.clientName}
                          </span>
                        </div>
                        {s.serviceName && (
                          <div className="flex items-center gap-1.5 text-xs">
                            <Stack className="size-3 text-muted-foreground" />
                            <span className="text-muted-foreground">{s.serviceName}</span>
                          </div>
                        )}
                      </div>

                      {/* Actions for scheduled sessions */}
                      {isScheduled && isRegular && s.cancelRequestId ? (
                        <div className="mt-3 flex flex-wrap items-center gap-2 pl-11">
                          <span className="rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-medium text-amber-800">
                            Otkazivanje u toku
                          </span>
                          {onWithdrawCancelRequest && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 rounded-full border-border/70 bg-transparent px-3 text-xs shadow-none"
                              onClick={() => onWithdrawCancelRequest(s)}
                            >
                              Povuci zahtjev
                            </Button>
                          )}
                        </div>
                      ) : (
                        (canReschedule || canCancel || canEditMembers) && (
                          <div className="mt-3 flex flex-wrap gap-1.5 pl-11">
                            {canReschedule && onReschedule && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 rounded-full border-border/70 bg-transparent px-3 text-xs shadow-none transition-all hover:border-primary/40 hover:bg-primary/5 hover:text-primary active:scale-[0.97]"
                                onClick={() => onReschedule(s)}
                              >
                                <CalendarDots className="size-3" />
                                Premjesti
                              </Button>
                            )}
                            {canEditMembers && onEditMembers && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 rounded-full border-border/70 bg-transparent px-3 text-xs shadow-none transition-all hover:border-primary/40 hover:bg-primary/5 hover:text-primary active:scale-[0.97]"
                                onClick={() => onEditMembers(s)}
                              >
                                <Users className="size-3" />
                                Članovi
                              </Button>
                            )}
                            {canCancel && onCancel && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 rounded-full border-destructive/25 bg-transparent px-3 text-xs text-destructive shadow-none transition-all hover:border-destructive/40 hover:bg-destructive/10 hover:text-destructive active:scale-[0.97]"
                                onClick={() => onCancel(s)}
                              >
                                <XCircle className="size-3" />
                                Otkaži
                              </Button>
                            )}
                          </div>
                        )
                      )}

                      {displayStatus === "hold" && onReleaseHold && (
                        <div className="mt-3 flex gap-2 pl-11">
                          <button className="text-xs text-red-600 hover:underline" onClick={() => onReleaseHold(s.id)}>{t("calendar.holdRelease")}</button>
                          <button className="text-xs text-amber-700 hover:underline" onClick={() => onExtendHold?.(s.id, 24)}>{t("calendar.holdExtend")}</button>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          )}
        </div>

        {/* Day summary */}
        {sessions.length > 0 && (
          <div className="border-t border-border/40 bg-card px-4 py-3">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">{t("calendar.totalSessions")}</span>
              <span className="font-semibold text-foreground">{sessions.length}</span>
            </div>
            <div className="mt-1 flex items-center justify-between text-xs">
              <span className="text-muted-foreground">{t("calendar.totalMinutes")}</span>
              <span className="font-semibold text-foreground">
                {totalMinutes} min
              </span>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}
